/**
 * Portal Authentication Integration
 *
 * This file handles authentication from the Terralink Portal.
 * When users click on BESS Dimension in the portal, they're redirected here
 * with a JWT token in the URL. This token contains their user info and is
 * validated against the portal backend.
 */

const PORTAL_API_URL = import.meta.env.VITE_PORTAL_API_URL || 'https://terralink-portal-api.onrender.com';
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://apps.terralink.cl';

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'operaciones' | 'ventas' | 'usuario';
}

export interface ValidationResponse {
  valid: boolean;
  user: PortalUser;
  appId: string;
  appName: string;
}

/**
 * Get session ID from cookie
 *
 * With shared cookie domain (.terralink.cl), all subdomains can read the session cookie
 */
export function getSessionCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'terralink_session') {
      return value;
    }
  }
  return null;
}

/**
 * Get token from URL or localStorage (fallback for non-terralink.cl domains)
 *
 * When redirected from portal: URL has ?token=...
 * On subsequent page loads: token is in localStorage
 */
export function getToken(): string | null {
  // Check URL first (initial redirect from portal)
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');

  if (tokenFromUrl) {
    // Store it and clean URL
    localStorage.setItem('portal_token', tokenFromUrl);
    window.history.replaceState({}, document.title, window.location.pathname);
    return tokenFromUrl;
  }

  // Check localStorage for existing token
  return localStorage.getItem('portal_token');
}

/**
 * Validate session with portal backend using session cookie
 *
 * The session cookie is automatically sent by the browser
 * Returns user information if session is valid
 */
export async function validateSession(): Promise<PortalUser | null> {
  try {
    // Get session ID from cookie
    const sessionId = getSessionCookie();

    if (!sessionId) {
      console.error('[Auth] No session cookie found');
      return null;
    }

    console.log('[Auth] Validating session with backend...');

    // Send session ID in Authorization header (cookies don't work cross-domain)
    // The backend API is on onrender.com, so cookies with domain .terralink.cl won't be sent
    const response = await fetch(`${PORTAL_API_URL}/api/auth/session`, {
      method: 'GET',
      credentials: 'include', // Try to send cookies (won't work cross-domain, but keep for compatibility)
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`, // Send session ID in header instead
      },
    });

    if (!response.ok) {
      console.error('[Auth] Session validation failed:', response.status);
      return null;
    }

    const data = await response.json();

    if (data.authenticated && data.user) {
      console.log('[Auth] Session valid, user:', data.user.email);
      return data.user;
    }

    return null;
  } catch (error) {
    console.error('[Auth] Session validation error:', error);
    return null;
  }
}

/**
 * Validate token with portal backend
 *
 * Sends token to backend API which:
 * - Verifies JWT signature
 * - Checks expiration
 * - Returns user information
 */
export async function validateToken(token: string): Promise<ValidationResponse | null> {
  try {
    const response = await fetch(`${PORTAL_API_URL}/api/auth/validate-app-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      console.error('[Auth] Token validation failed:', response.status);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Auth] Token validation error:', error);
    return null;
  }
}

/**
 * Clear authentication and redirect back to portal
 *
 * Used when:
 * - Token is invalid/expired
 * - User clicks "logout"
 * - No token found
 */
export function redirectToPortal() {
  localStorage.removeItem('portal_token');
  window.location.href = PORTAL_URL;
}

/**
 * Check if user has required role
 *
 * Usage:
 *   if (hasRole(user, ['admin', 'operaciones'])) {
 *     // Show admin-only feature
 *   }
 */
export function hasRole(user: PortalUser, roles: string[]): boolean {
  return roles.includes(user.role);
}
